import { DAY_LABELS, type OpeningHours, type TimeRange } from '@gl/shared';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

/** Lunes primero, domingo al final. */
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export interface DayState {
  day: number;
  open: boolean;
  ranges: TimeRange[];
}

export function hoursToState(hours: OpeningHours): DayState[] {
  return DAY_ORDER.map((day) => {
    const found = hours.find((h) => h.day === day);
    return { day, open: !!found && found.ranges.length > 0, ranges: found?.ranges.length ? found.ranges : [{ open: '12:00', close: '23:00' }] };
  });
}

export function stateToHours(state: DayState[]): OpeningHours {
  return state.filter((d) => d.open).map((d) => ({ day: d.day, ranges: d.ranges }));
}

export function HoursEditor({
  value,
  onChange,
  disabled,
}: {
  value: DayState[];
  onChange: (next: DayState[]) => void;
  disabled?: boolean;
}) {
  const update = (day: number, fn: (d: DayState) => DayState) =>
    onChange(value.map((d) => (d.day === day ? fn(d) : d)));

  return (
    <div className="divide-y">
      {value.map((d) => (
        <div key={d.day} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
          <label className="flex w-36 shrink-0 items-center gap-3 pt-2 text-sm font-medium">
            <Switch
              checked={d.open}
              disabled={disabled}
              onCheckedChange={(open) => update(d.day, (x) => ({ ...x, open }))}
            />
            {DAY_LABELS[d.day]}
          </label>
          {d.open ? (
            <div className="flex flex-1 flex-col gap-2">
              {d.ranges.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={r.open}
                    disabled={disabled}
                    className="w-28"
                    aria-label="Abre"
                    onChange={(e) =>
                      update(d.day, (x) => ({
                        ...x,
                        ranges: x.ranges.map((rr, j) => (j === i ? { ...rr, open: e.target.value } : rr)),
                      }))
                    }
                  />
                  <span className="text-sm text-muted-foreground">a</span>
                  <Input
                    type="time"
                    value={r.close}
                    disabled={disabled}
                    className="w-28"
                    aria-label="Cierra"
                    onChange={(e) =>
                      update(d.day, (x) => ({
                        ...x,
                        ranges: x.ranges.map((rr, j) => (j === i ? { ...rr, close: e.target.value } : rr)),
                      }))
                    }
                  />
                  {d.ranges.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      disabled={disabled}
                      aria-label="Quitar franja"
                      onClick={() => update(d.day, (x) => ({ ...x, ranges: x.ranges.filter((_, j) => j !== i) }))}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>
              ))}
              {d.ranges.length < 4 && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="w-fit px-0"
                  disabled={disabled}
                  onClick={() => update(d.day, (x) => ({ ...x, ranges: [...x.ranges, { open: '19:00', close: '23:30' }] }))}
                >
                  <Plus /> Agregar franja
                </Button>
              )}
            </div>
          ) : (
            <p className="pt-2 text-sm text-muted-foreground">Cerrado</p>
          )}
        </div>
      ))}
    </div>
  );
}
